// translate.directive.ts
import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  Host,
  Inject,
  inject,
  Input,
  Optional,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { AuthGuardService } from '@app/services/auth-guard.service';
// import { I18nService } from "@app/services/i18n.service";
import { TranslateService } from '@app/services/translate/translate.service';

@Directive({
  selector: '[multilang]',
  providers: [],
  standalone: false,
})
export class TranslateDirective implements AfterViewInit {
  @Input('multilang') set multilang(value: boolean | string) {
    this.enabled = value !== false && value !== 'false';
  }
  @Input() deeplMode: 'text' | 'html' | 'placeholder' | 'tooltip' | 'title' =
    'text';
  @Input() defaultlanguage: string = 'de';
  private enabled = true;
  isBrowser = signal<boolean>(false);
  destroyRef = inject(DestroyRef);
  targetLanguage: string = 'de';
  // historyNodes: Text[] = [];

  observer: any;
  private translated = [];
  private translationInitialized = false;

  constructor(
    private el: ElementRef,
    @Inject(PLATFORM_ID) protected platformId: any,
    protected translateService: TranslateService,
    protected guardService: AuthGuardService,
    @Host() @Optional() private tooltip: MatTooltip,
    // private i18nService: I18nService,
  ) {
    this.isBrowser.set(isPlatformBrowser(this.platformId));
  }

  ngAfterViewInit() {
    if (!this.isBrowser()) return;
    if (!this.enabled) return;
    this.guardService.getPartner$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((partner) => {
        if (!partner?.multilangualktiv || this.translationInitialized) {
          return;
        }
        this.translationInitialized = true;
        this.initTranslation();
      });
  }

  initTranslation() {
    if (this.deeplMode === 'html') {
      // -------- INNERHTML ----------
      this.processTextNodes(this.el.nativeElement);
      this.observer = new MutationObserver(() => {
        this.processTextNodes(this.el.nativeElement);
      });
      // observer without  characterData: true,
      this.observer.observe(this.el.nativeElement, {
        childList: true,
        subtree: true,
      });
      this.destroyRef.onDestroy(() => this.observer.disconnect());
    } else {
      this.processTextNodes(this.el.nativeElement);
      this.observer = new MutationObserver(() => {
        this.processTextNodes(this.el.nativeElement);
      });

      this.observer.observe(this.el.nativeElement, {
        characterData: true,
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['title', 'placeholder'],
      });
      this.destroyRef.onDestroy(() => this.observer.disconnect());
    }

    // this.checkLanguage();
  }

  // checkLanguage() {
  //     this.i18nService.langOb.pipe(
  //         takeUntilDestroyed(this.destroyRef)
  //     ).subscribe(lg => {
  //         this.currentLang = lg;
  //     })
  // }

  processTextNodes(element) {
    const textNodes: any[] = [];

    if (this.deeplMode === 'tooltip' && this.tooltip?.message) {
      textNodes.push(this.tooltip.message);
    }
    if (this.deeplMode === 'placeholder') {
      const plh = this.el.nativeElement.getAttribute('placeholder') || '';
      if (plh?.length > 0 && !this.translated.includes(plh)) {
        textNodes.push(this.el.nativeElement);
      }
    } else if (this.deeplMode === 'title') {
      const title = this.el.nativeElement.getAttribute('title') || '';
      if (title?.length > 0 && !this.translated.includes(title)) {
        textNodes.push(this.el.nativeElement);
      }
    } else {
      // Create a TreeWalker to find all visible text nodes
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          // Filter out whitespace-only nodes
          return node.textContent
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      });

      let currentNode: Text | null;
      // console.log(currentNode?.nodeValue, this.translated, currentNode?.nodeValue === this.translated)
      while ((currentNode = walker.nextNode() as Text | null)) {
        if (this.translated.indexOf(currentNode?.nodeValue) < 0) {
          textNodes.push(currentNode as Text);
        }
      }
    }

    // Translate each text node individually
    textNodes.forEach((node) => {
      let originalText = '';
      if (this.deeplMode === 'title') {
        originalText = node.getAttribute('title');
      } else if (this.deeplMode === 'tooltip') {
        originalText = node;
      } else if (this.deeplMode === 'placeholder') {
        originalText = node.getAttribute('placeholder') || '';
      } else {
        originalText = node.nodeValue;
      }

      if (originalText?.length > 0) {
        this.translateService.collect(
          originalText,
          (translated) => {
            // console.log(translated)
            if (translated?.length > 0) {
              if (this.deeplMode === 'tooltip') {
                this.tooltip.message = translated;
              } else if (this.deeplMode === 'title') {
                node.title = translated;
              } else if (this.deeplMode === 'placeholder') {
                node.placeholder = translated;
              } else {
                node.textContent = translated;
              }
              // this.observer.disconnect();
              this.translated.push(translated);
            }
          },
          this.defaultlanguage,
        );
      }
    });
  }
}
